const KL_ENGRAVE_SELECTOR = '[data-kl-engrave]';
const KL_ENGRAVE_SCREEN_SELECTOR = '[data-kl-engrave-screen]';
const KL_ENGRAVE_TAB_SELECTOR = '[data-kl-engrave-tab]';
const KL_ENGRAVE_FONT_SELECTOR = '[data-kl-engrave-font]';
const KL_ENGRAVE_GREEK_GRID_SELECTOR = '[data-kl-engrave-greek-grid]';
const KL_ENGRAVE_SUBMIT_SELECTOR = '[type="submit"][name="add"]';
const KL_ENGRAVE_SELECTED_VARIANT_SELECTOR = '[data-selected-variant]';

/*
 * registerGate, hasGateCleared, markGateCleared and clearGateStateOnRealSubmit
 * come from assets/kl-form-gate.js, loaded first — see that file for what the
 * 'engrave' gate name below is coordinating with.
 */

const EMOJI_PATTERN =
  /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu;

function filterEmojis(text) {
  return text.replace(EMOJI_PATTERN, '');
}

function readGreekLetters(root) {
  const script = root.querySelector('[data-kl-engrave-greek-letters]');

  if (!script) return [];

  try {
    return JSON.parse(script.textContent);
  } catch (error) {
    console.debug('kl-engrave: the Greek letter list could not be read', { error });
    return [];
  }
}

function readSelectedVariant(root) {
  const variantHost = document.getElementById(root.dataset.klEngraveVariants || '');
  const script = variantHost?.querySelector(KL_ENGRAVE_SELECTED_VARIANT_SELECTOR);

  if (!script) return null;

  try {
    return JSON.parse(script.textContent);
  } catch (error) {
    console.debug('kl-engrave: the selected variant could not be read', { error });
    return null;
  }
}

function readGallery(root) {
  const script = root.querySelector('[data-kl-engrave-gallery]');

  if (!script) return null;

  try {
    return JSON.parse(script.textContent);
  } catch (error) {
    console.debug('kl-engrave: the product gallery could not be read', { error });
    return null;
  }
}

/*
 * Merchants append one blank, unengraved photo per variant, in variant order, to
 * the end of the product's image gallery — the variant's own featured_image can
 * show a pre-engraved sample, so it is not used here. imageUrls' trailing N
 * entries are assumed to line up 1:1 with variantIds, in order; the selected
 * variant's position in variantIds picks among them. Falls back to the gallery's
 * last image when there is no variant to match against or too few trailing photos.
 */
function pickPreviewImageUrl(gallery, selectedVariantId) {
  if (!gallery?.imageUrls?.length) return null;

  const { imageUrls, variantIds = [] } = gallery;
  const fallback = imageUrls[imageUrls.length - 1];

  if (variantIds.length <= 1 || imageUrls.length < variantIds.length) return fallback;

  const selectedIndex = variantIds.map(String).indexOf(String(selectedVariantId));

  if (selectedIndex === -1) return fallback;

  const mapStartIndex = imageUrls.length - variantIds.length;

  return imageUrls[mapStartIndex + selectedIndex] || fallback;
}

function showScreen(root, refs, screenName) {
  refs.screens.forEach((screen) => {
    screen.hidden = screen.dataset.klEngraveScreen !== screenName;
  });

  if (refs.closeButton) refs.closeButton.hidden = screenName === 'review';

  if (screenName === 'design') {
    const activeTab = root.querySelector(`${KL_ENGRAVE_TAB_SELECTOR}[aria-selected="true"]`);
    const focusTarget = activeTab?.dataset.klEngraveTab === 'greek' ? null : refs.input;

    window.requestAnimationFrame(() => focusTarget?.focus());
  }
}

function setActiveTab(root, refs, tabName) {
  refs.tabs.forEach((tab) => {
    tab.setAttribute('aria-selected', String(tab.dataset.klEngraveTab === tabName));
  });

  refs.greekGrid.hidden = tabName !== 'greek';
  refs.preview.classList.toggle('kl-engrave__preview--greek', tabName === 'greek');

  if (refs.fontPicker) refs.fontPicker.hidden = tabName === 'greek';

  if (tabName === 'greek') {
    window.requestAnimationFrame(() => refs.input?.focus());
  }
}

const ENGRAVE_FONT_NAMES = ['saol', 'angelface', 'brandon'];

function setActiveFont(root, refs, fontName) {
  refs.fontButtons.forEach((button) => {
    button.setAttribute('aria-pressed', String(button.dataset.klEngraveFont === fontName));
  });

  refs.input.dataset.klEngraveActiveFont = fontName;
  ENGRAVE_FONT_NAMES.forEach((name) => {
    refs.input.classList.toggle(`kl-engrave__input--font-${name}`, name === fontName);
  });
}

function updateSaveEnabled(refs) {
  refs.saveButton.disabled = refs.input.value.trim() === '';
}

function buildGreekGrid(refs, greekLetters, maxLength) {
  const fragment = document.createDocumentFragment();

  greekLetters.forEach((letter) => {
    const tile = document.createElement('button');

    tile.type = 'button';
    tile.className = 'kl-engrave__greek-tile';
    tile.dataset.klEngraveGreekTile = letter.snippet;

    let glyph;

    if (letter.image) {
      glyph = document.createElement('img');
      glyph.className = 'kl-engrave__greek-glyph';
      glyph.src = letter.image;
      glyph.alt = '';
      glyph.loading = 'lazy';
    } else {
      glyph = document.createElement('span');
      glyph.className = 'kl-engrave__greek-glyph kl-engrave__greek-glyph--text';
      glyph.textContent = letter.snippet;
    }

    const label = document.createElement('span');

    label.className = 'kl-engrave__greek-label';
    label.textContent = letter.title;

    tile.append(glyph, label);

    tile.addEventListener('click', () => {
      if (refs.input.value.length >= maxLength) return;

      refs.input.value += letter.snippet;
      refs.input.dispatchEvent(new Event('input', { bubbles: true }));
    });

    fragment.append(tile);
  });

  refs.greekGrid.append(fragment);
}

function updateReviewScreen(refs) {
  if (refs.reviewText) {
    refs.reviewText.textContent = refs.input.value;

    const activeFont = refs.input.dataset.klEngraveActiveFont;

    ENGRAVE_FONT_NAMES.forEach((name) => {
      refs.reviewText.classList.toggle(`kl-engrave__input--font-${name}`, name === activeFont);
    });
  }

  if (refs.reviewImage && refs.image?.currentSrc) {
    refs.reviewImage.src = refs.image.currentSrc;
  }
}

function writeEngravingProperties(root, refs, form) {
  const textPropertyName = root.dataset.klEngraveTextProperty;
  const fontPropertyName = root.dataset.klEngraveFontProperty;
  const fontValue = refs.input.dataset.klEngraveActiveFont || 'saol';

  [
    { name: textPropertyName, value: refs.input.value },
    { name: fontPropertyName, value: fontValue },
  ].forEach(({ name, value }) => {
    if (!name) return;

    let field = form.querySelector(`input[name="properties[${CSS.escape(name)}]"]`);

    if (!field) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = `properties[${name}]`;
      form.append(field);
    }

    field.value = value;
  });
}

function replaySubmit(form) {
  const submitButton = form.querySelector(KL_ENGRAVE_SUBMIT_SELECTOR);

  if (submitButton && !submitButton.disabled) {
    form.requestSubmit(submitButton);
    return;
  }

  form.requestSubmit();
}

function updatePreviewImage(root, refs) {
  const variant = readSelectedVariant(root);
  const selectedVariantId = variant?.id;
  const imageSource = pickPreviewImageUrl(refs.gallery, selectedVariantId);

  if (!imageSource) return;

  [refs.image, refs.reviewImage].forEach((image) => {
    if (image) image.src = imageSource;
  });
}

function initEngrave(root) {
  if (!root || root.dataset.klEngraveReady === 'true') return;

  const dialog = root.closest('dialog');
  const form = document.getElementById(root.dataset.klEngraveForm || '');

  if (!dialog || !form) {
    console.debug('kl-engrave: the dialog or the product form could not be found', {
      formId: root.dataset.klEngraveForm,
    });
    return;
  }

  const refs = {
    screens: Array.from(root.querySelectorAll(KL_ENGRAVE_SCREEN_SELECTOR)),
    tabs: Array.from(root.querySelectorAll(KL_ENGRAVE_TAB_SELECTOR)),
    fontButtons: Array.from(root.querySelectorAll(KL_ENGRAVE_FONT_SELECTOR)),
    greekGrid: root.querySelector(KL_ENGRAVE_GREEK_GRID_SELECTOR),
    fontPicker: root.querySelector('[data-kl-engrave-font-picker]'),
    closeButton: root.querySelector('[data-kl-engrave-close]'),
    input: root.querySelector('[data-kl-engrave-input]'),
    image: root.querySelector('[data-kl-engrave-image]'),
    reviewImage: root.querySelector('[data-kl-engrave-review-image]'),
    reviewText: root.querySelector('[data-kl-engrave-review-text]'),
    preview: root.querySelector('.kl-engrave__preview'),
    saveButton: root.querySelector('[data-kl-engrave-save]'),
    exitButtons: Array.from(root.querySelectorAll('[data-kl-engrave-exit]')),
    backButton: root.querySelector('[data-kl-engrave-back]'),
    reviewBackButton: root.querySelector('[data-kl-engrave-review-back]'),
    confirmButton: root.querySelector('[data-kl-engrave-confirm]'),
    openProxy: root.querySelector('[data-kl-engrave-open-proxy]'),
    gallery: readGallery(root),
  };

  if (!refs.input || !refs.saveButton || !refs.openProxy) {
    console.debug('kl-engrave: the textarea, save button or dialog trigger could not be found');
    return;
  }

  root.dataset.klEngraveReady = 'true';

  const maxLength = Number.parseInt(root.dataset.klEngraveMaxLength, 10) || 17;

  buildGreekGrid(refs, readGreekLetters(root), maxLength);
  setActiveFont(root, refs, 'brandon');

  refs.tabs.forEach((tab) => {
    tab.addEventListener('click', () => setActiveTab(root, refs, tab.dataset.klEngraveTab));
  });

  refs.fontButtons.forEach((button) => {
    button.addEventListener('click', () => setActiveFont(root, refs, button.dataset.klEngraveFont));
  });

  refs.input.addEventListener('input', () => {
    const filtered = filterEmojis(refs.input.value);

    if (filtered !== refs.input.value) refs.input.value = filtered;

    updateSaveEnabled(refs);
  });

  refs.saveButton.addEventListener('click', () => {
    if (refs.saveButton.disabled) return;

    updateReviewScreen(refs);
    showScreen(root, refs, 'review');
  });

  refs.exitButtons.forEach((button) => {
    button.addEventListener('click', () => showScreen(root, refs, 'exit'));
  });
  refs.backButton.addEventListener('click', () => showScreen(root, refs, 'design'));
  refs.reviewBackButton.addEventListener('click', () => showScreen(root, refs, 'design'));

  clearGateStateOnRealSubmit(form);
  registerGate(form, 'engrave');

  refs.confirmButton.addEventListener('click', () => {
    writeEngravingProperties(root, refs, form);
    dialog.close();
    markGateCleared(form, 'engrave');
    replaySubmit(form);
  });

  dialog.addEventListener('close', () => {
    showScreen(root, refs, 'design');
  });

  const variantHost = document.getElementById(root.dataset.klEngraveVariants || '');

  updatePreviewImage(root, refs);
  variantHost?.addEventListener('change', () => updatePreviewImage(root, refs));

  document.addEventListener(
    'submit',
    (event) => {
      if (event.target !== form || hasGateCleared(form, 'engrave')) return;

      event.preventDefault();
      event.stopPropagation();

      if (dialog.open) return;

      showScreen(root, refs, 'design');

      try {
        refs.openProxy.click();
      } catch (error) {
        console.debug('kl-engrave: the dialog could not be opened', { error });
      }
    },
    true
  );
}

document.querySelectorAll(KL_ENGRAVE_SELECTOR).forEach(initEngrave);

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll(KL_ENGRAVE_SELECTOR).forEach(initEngrave);
});
